import { X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ButtonHTMLAttributes, KeyboardEvent, PointerEvent, ReactNode } from "react";

export type Detent = "peek" | "medium" | "large";
export const DETENTS: readonly Detent[] = ["peek", "medium", "large"];

export const PEEK_PX = 96;

/** Detent heights in px for a viewport height: peek 96px, medium ~50%, large ~92%. */
export function detentHeights(viewportH: number): Record<Detent, number> {
  return {
    peek: PEEK_PX,
    medium: Math.round(viewportH * 0.5),
    large: Math.round(viewportH * 0.92),
  };
}

export function useViewportHeight(): number {
  const [h, setH] = useState(() => (typeof window === "undefined" ? 800 : window.innerHeight));
  useEffect(() => {
    const on = () => setH(window.innerHeight);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return h;
}

/**
 * HIG-style header control: a 30 px filled circle inside a 44 px hit area, 16 px glyph with a 2 px
 * stroke. Used for close, collapse and expand, so they all share size, weight and alignment.
 */
export function SheetButton({
  icon,
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label" | "children"> & {
  "aria-label": string;
  icon: ReactNode;
}) {
  return (
    <button type="button" className="sheet-btn" title={rest["aria-label"]} {...rest}>
      <span className="sheet-btn__disc" aria-hidden="true">
        {icon}
      </span>
    </button>
  );
}

const isDesktop = () =>
  typeof window !== "undefined" && window.matchMedia?.("(min-width: 900px)").matches === true;

interface Props {
  /** Accessible name of the panel (the spot name). */
  label: string;
  detent: Detent;
  onDetentChange: (d: Detent) => void;
  onClose: () => void;
  labels: {
    grabber: string;
    close: string;
    detents: Record<Detent, string>;
  };
  /** Extra control beside the close button (desktop panel collapse). */
  headerAction?: ReactNode;
  children: ReactNode;
}

/**
 * Design `Sheet`: a bottom sheet with three detents on phones (draggable grabber, tap or
 * arrow keys cycle) and a 380px floating side panel from 900px. Esc closes on desktop and
 * steps down a detent on phones.
 */
export function Sheet({
  label,
  detent,
  onDetentChange,
  onClose,
  labels,
  headerAction,
  children,
}: Props) {
  const vh = useViewportHeight();
  const heights = detentHeights(vh);
  const [dragH, setDragH] = useState<number | null>(null);
  const drag = useRef<{
    startY: number;
    startH: number;
    lastY: number;
    lastT: number;
    v: number;
    moved: boolean;
  } | null>(null);

  const idx = DETENTS.indexOf(detent);
  const go = useCallback(
    (i: number) =>
      onDetentChange(DETENTS[Math.max(0, Math.min(DETENTS.length - 1, i))] ?? "medium"),
    [onDetentChange],
  );

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (isDesktop() || detent === "peek") onClose();
      else go(idx - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detent, idx, go, onClose]);

  const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = {
      startY: e.clientY,
      startH: heights[detent],
      lastY: e.clientY,
      lastT: e.timeStamp,
      v: 0,
      moved: false,
    };
  };
  const onPointerMove = (e: PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d) return;
    const dy = d.startY - e.clientY;
    if (Math.abs(dy) > 6) d.moved = true;
    if (!d.moved) return;
    const dt = Math.max(1, e.timeStamp - d.lastT);
    d.v = (d.lastY - e.clientY) / dt; // px/ms, positive = moving up
    d.lastY = e.clientY;
    d.lastT = e.timeStamp;
    setDragH(Math.max(heights.peek, Math.min(heights.large, d.startH + dy)));
  };
  const finishDrag = (e: PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId))
      e.currentTarget.releasePointerCapture(e.pointerId);
    if (!d.moved) {
      go(idx >= DETENTS.length - 1 ? 0 : idx + 1); // tap cycles peek -> medium -> large -> peek
      setDragH(null);
      return;
    }
    const projected = (dragH ?? d.startH) + d.v * 160;
    const nearest = DETENTS.reduce(
      (best, k) =>
        Math.abs(heights[k] - projected) < Math.abs(heights[best] - projected) ? k : best,
      detent,
    );
    onDetentChange(nearest);
    setDragH(null);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const map: Record<string, number | undefined> = {
      ArrowUp: idx + 1,
      ArrowDown: idx - 1,
      Home: 0,
      End: DETENTS.length - 1,
      Enter: idx >= DETENTS.length - 1 ? 0 : idx + 1,
      " ": idx >= DETENTS.length - 1 ? 0 : idx + 1,
    };
    const next = map[e.key];
    if (next !== undefined) {
      e.preventDefault();
      go(next);
    }
  };

  const height = dragH ?? heights[detent];

  return (
    <aside
      className="spot-sheet ds-sheet ds-glass"
      aria-label={label}
      data-detent={detent}
      data-controls={headerAction ? "2" : "1"}
      data-dragging={dragH !== null}
      style={{ "--sheet-h": `${height}px` } as React.CSSProperties}
    >
      <button
        type="button"
        className="spot-sheet__handle"
        role="slider"
        aria-label={labels.grabber}
        aria-orientation="vertical"
        aria-valuemin={0}
        aria-valuemax={DETENTS.length - 1}
        aria-valuenow={idx}
        aria-valuetext={labels.detents[detent]}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
        onKeyDown={onKeyDown}
      >
        <span className="ds-sheet__grabber" />
      </button>
      <div className="spot-sheet__controls">
        {headerAction}
        <SheetButton
          aria-label={labels.close}
          icon={<X aria-hidden="true" size={16} strokeWidth={2} />}
          onClick={onClose}
        />
      </div>
      <div className="spot-sheet__scroll glass-scroll">{children}</div>
    </aside>
  );
}
